/**
 * @file useBuildingApi.js
 * @description API composable for building/level/space CRUD operations
 * Handles all HTTP requests related to site hierarchy entities
 */

import { ref, toValue } from 'vue';
import { useQuasar } from 'quasar';
import brickSchemaService from '@/services/brickSchemaService';

/**
 * Composable for building/level/space API operations
 * @param {number|Ref<number>} siteId - The site ID (can be a ref or computed)
 * @returns {Object} Building API methods and state
 */
export function useBuildingApi(siteId) {
  const $q = useQuasar();

  // State
  const loadingHierarchy = ref(false);
  const saving = ref(false);
  const localHierarchyData = ref(null);

  /**
   * Load complete site hierarchy (buildings, levels, spaces)
   * @param {boolean} bypassCache - Force fresh data from server
   * @returns {Promise<Object>} Hierarchy data
   */
  async function loadHierarchyData(bypassCache = false) {
    const unwrappedSiteId = toValue(siteId);
    if (!unwrappedSiteId) {
      return null;
    }

    try {
      loadingHierarchy.value = true;

      const result = await brickSchemaService.hierarchy.get(unwrappedSiteId, bypassCache);

      if (!result.success) {
        throw new Error(result.error);
      }

      localHierarchyData.value = result.data || null;
      return localHierarchyData.value;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load site hierarchy: ${error.message}`,
      });
      return null;
    } finally {
      loadingHierarchy.value = false;
    }
  }

  /**
   * Create new building
   * @param {Object} buildingData - Building data to create
   * @returns {Promise<Object>} Created building
   */
  async function createBuilding(buildingData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.hierarchy.createBuilding(toValue(siteId), buildingData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newBuilding = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Building "${buildingData.name}" created successfully`,
      });

      return newBuilding;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create building: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update existing building
   * @param {number} buildingId - Building ID
   * @param {Object} buildingData - Updated building data
   * @returns {Promise<Object>} Updated building
   */
  async function updateBuilding(buildingId, buildingData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.hierarchy.updateBuilding(toValue(siteId), buildingId, buildingData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedBuilding = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Building updated successfully',
      });

      return updatedBuilding;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update building: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Delete building
   * @param {number} buildingId - Building ID to delete
   * @param {boolean} cascade - Delete children recursively
   * @returns {Promise<void>}
   */
  async function deleteBuilding(buildingId, cascade = false) {
    try {
      const result = await brickSchemaService.hierarchy.deleteBuilding(toValue(siteId), buildingId, cascade);

      if (!result.success) {
        throw new Error(result.error);
      }

      $q.notify({
        type: 'positive',
        message: 'Building deleted successfully',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to delete building: ${error.message}`,
      });
      throw error;
    }
  }

  /**
   * Create new level
   * @param {number} buildingId - Parent building ID
   * @param {Object} levelData - Level data to create
   * @returns {Promise<Object>} Created level
   */
  async function createLevel(buildingId, levelData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.hierarchy.createLevel(toValue(siteId), buildingId, levelData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newLevel = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Level "${levelData.name}" created successfully`,
      });

      return newLevel;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create level: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update existing level
   * @param {number} levelId - Level ID
   * @param {Object} levelData - Updated level data
   * @returns {Promise<Object>} Updated level
   */
  async function updateLevel(levelId, levelData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.hierarchy.updateLevel(toValue(siteId), levelId, levelData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedLevel = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Level updated successfully',
      });

      return updatedLevel;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update level: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Delete level
   * @param {number} levelId - Level ID to delete
   * @param {boolean} cascade - Delete children recursively
   * @returns {Promise<void>}
   */
  async function deleteLevel(levelId, cascade = false) {
    try {
      const result = await brickSchemaService.hierarchy.deleteLevel(toValue(siteId), levelId, cascade);

      if (!result.success) {
        throw new Error(result.error);
      }

      $q.notify({
        type: 'positive',
        message: 'Level deleted successfully',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to delete level: ${error.message}`,
      });
      throw error;
    }
  }

  /**
   * Create new space
   * @param {number} levelId - Parent level ID
   * @param {Object} spaceData - Space data to create
   * @returns {Promise<Object>} Created space
   */
  async function createSpace(levelId, spaceData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.hierarchy.createSpace(toValue(siteId), levelId, spaceData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newSpace = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Space "${spaceData.name}" created successfully`,
      });

      return newSpace;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create space: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update existing space
   * @param {number} spaceId - Space ID
   * @param {Object} spaceData - Updated space data
   * @returns {Promise<Object>} Updated space
   */
  async function updateSpace(spaceId, spaceData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.hierarchy.updateSpace(toValue(siteId), spaceId, spaceData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedSpace = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Space updated successfully',
      });

      return updatedSpace;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update space: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Delete space
   * @param {number} spaceId - Space ID to delete
   * @param {boolean} cascade - Delete children recursively
   * @returns {Promise<void>}
   */
  async function deleteSpace(spaceId, cascade = false) {
    try {
      const result = await brickSchemaService.hierarchy.deleteSpace(toValue(siteId), spaceId, cascade);

      if (!result.success) {
        throw new Error(result.error);
      }

      $q.notify({
        type: 'positive',
        message: 'Space deleted successfully',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to delete space: ${error.message}`,
      });
      throw error;
    }
  }

  return {
    // State
    loadingHierarchy,
    saving,
    localHierarchyData,

    // Methods
    loadHierarchyData,
    createBuilding,
    updateBuilding,
    deleteBuilding,
    createLevel,
    updateLevel,
    deleteLevel,
    createSpace,
    updateSpace,
    deleteSpace,
  };
}
